import React from 'react';
import { Link } from 'react-router-dom';
import { useContentOverrides } from '../../lib/useContentOverrides';

// Vibrant 4K Pexels imagery — matches the Europe carousel quality bar.
const pexels = (id, ext = 'jpeg') =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.${ext}?auto=compress&cs=tinysrgb&w=1600&fit=max`;

// Cinematic luxury hero video — slow aerial over a tropical resort pool.
const HERO_VIDEO = 'https://videos.pexels.com/video-files/32947362/14041585_1920_1080_25fps.mp4';
const HERO_POSTER = 'https://images.pexels.com/videos/32947362/pexels-photo-32947362.jpeg?auto=compress&cs=tinysrgb&w=1920';

// Canonical, hardcoded source of truth — every field is overridable
// via /api/site-content/public/services, but if the override is missing
// or the API is slow, the page still renders these values byte-for-byte.
const SERVICES_DEFAULT = [
  {
    title: 'Bespoke Trip Design',
    desc: 'Fully customized itineraries built around your interests, pace, and rhythm \u2014 not a template.',
    details: ['Multi-destination planning', 'Accommodation sourcing', 'Activity curation', 'Local guide coordination'],
    image: pexels(3601422),
    alt: 'Aerial view of luxury overwater villas in the Maldives',
  },
  {
    title: 'Honeymoons & Celebrations',
    desc: 'Milestone journeys designed with care, surprise, and emotional intelligence.',
    details: ['Anniversary trips', 'Birthday celebrations', 'Proposal planning', 'Group coordination'],
    image: pexels(12858541),
    alt: 'A celebratory moment captured during a milestone journey',
  },
  {
    title: 'Family Travel',
    desc: 'Multi-generational travel where every detail is considered for every traveler.',
    details: ['Kid-friendly itineraries', 'Multigenerational logistics', 'Activity planning by age', 'Family reunion coordination'],
    image: pexels(29855986),
    alt: 'A family enjoying a shared travel moment',
  },
  {
    title: 'Group Travel',
    desc: 'Friends, milestones, or extended family \u2014 coordinated as one seamless journey, with care for every detail.',
    details: ['Milestone group trips', 'Friends & extended family', 'Private group experiences', 'Coordinated logistics across travelers'],
    image: pexels(5677279),
    alt: 'A coordinated group experience while traveling together',
  },
  {
    title: 'Adventure & Expedition',
    desc: 'Off-the-beaten-path journeys for those who seek depth over familiarity.',
    details: ['Safari planning', 'Trekking expeditions', 'Diving & marine travel', 'Polar & extreme destinations'],
    image: pexels(28157155),
    alt: 'Safari adventure at sunset in the African savannah',
  },
  {
    title: 'Concierge & On-Trip Support',
    desc: 'Real-time assistance throughout your journey \u2014 discreet, efficient, and always available.',
    details: ['24/7 advisor availability', 'Restaurant reservations', 'Last-minute changes', 'Emergency support'],
    image: pexels(3769980),
    alt: 'Hotel room service in a luxurious interior',
  },
];

export default function ServicesPage() {
  const c = useContentOverrides('services');
  // Schema: each service item is fully overridable, including order.
  // The override list (when present) wins; otherwise fall back to the
  // hardcoded six.  Layout is unchanged either way.
  const services = c.get('services', SERVICES_DEFAULT);
  const heroKicker = c.get('hero.kicker', 'Our Services');
  const heroTitle = c.get('hero.title', 'What We Do');
  const heroSubtitle = c.get(
    'hero.subtitle',
    'End-to-end luxury travel planning, from first conversation to welcome home.',
  );
  const ctaKicker = c.get('cta.kicker', 'The Next Step');
  const ctaMessage = c.get('cta.message', 'Interested? Let us know what you have in mind.');
  const ctaButton = c.get('cta.button_label', 'Begin Your Experience');
  return (
    <div data-testid="page-services">
      {c.previewMode && (
        <div
          className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono"
          data-testid="services-preview-banner"
        >
          Studio draft preview \u2014 not yet published
        </div>
      )}
      {/* Hero */}
      <section className="pt-20 pb-16 sm:pt-32 sm:pb-24 px-6 text-center" data-testid="services-hero">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{heroKicker}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] max-w-2xl mx-auto leading-tight">
          {heroTitle}
        </h1>
        <div className="w-12 h-px bg-[#E8E4DC] mx-auto my-6 sm:my-8" aria-hidden="true" />
        <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] max-w-xl mx-auto leading-relaxed">
          {heroSubtitle}
        </p>
      </section>

      {/* Cinematic Hero Video \u2014 contained inside an editorial frame */}
      <section className="bg-white border-t border-[#E8E4DC] py-10 sm:py-16 px-6" data-testid="services-hero-video-section">
        <div className="max-w-5xl mx-auto">
          <video
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="aspect-[3/1] w-full object-cover rounded"
            data-testid="services-hero-video"
          />
        </div>
      </section>

      {/* Alternating Service Sections */}
      <section className="bg-[#FAFAF8] border-t border-[#E8E4DC] py-8 sm:py-16 px-6" data-testid="services-categories">
        <div className="max-w-5xl mx-auto">
          {services.map((svc, i) => {
            const imageFirst = i % 2 === 0;
            return (
              <div key={`${svc.title}-${i}`} className="py-12 sm:py-20 border-b border-[#E8E4DC] last:border-b-0" data-testid={`service-${i}`}>
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center ${!imageFirst ? 'md:[direction:rtl]' : ''}`}>
                  {/* Image */}
                  <div className={!imageFirst ? 'md:[direction:ltr]' : ''}>
                    <img
                      src={svc.image}
                      alt={svc.alt}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover rounded"
                      data-testid={`service-${i}-image`}
                    />
                  </div>

                  {/* Text Content */}
                  <div className={!imageFirst ? 'md:[direction:ltr]' : ''}>
                    <p className="text-[11px] text-[#C9A961] tracking-[0.22em] font-medium mb-3">{String(i + 1).padStart(2, '0')}</p>
                    <h2 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] mb-4 leading-snug">{svc.title}</h2>
                    <p className="text-sm text-[#6B6B6B] mb-6 leading-relaxed">{svc.desc}</p>
                    <ul className="space-y-1.5">
                      {(svc.details || []).map((d, di) => (
                        <li key={`${d}-${di}`} className="text-xs text-[#999]">{d}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA \u2014 Filled Black */}
      <section className="py-16 sm:py-24 px-6 text-center bg-[#F5F3EE] border-t border-[#E8E4DC]" data-testid="services-cta">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{ctaKicker}</p>
        <p className="font-accent text-base sm:text-lg text-[#1A1A1A] italic mb-10">
          {ctaMessage}
        </p>
        <Link
          to="/plan"
          className="px-8 py-3.5 bg-[#1A1A1A] text-white text-sm tracking-wide rounded hover:bg-[#333] transition-colors"
          data-testid="services-cta-button"
        >
          {ctaButton}
        </Link>
      </section>
    </div>
  );
}
